import { useEffect, useMemo, useRef } from 'react';
import { ChevronDown, Server, Cloud, Cpu, Wallet, AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getModelCatalog, LLMModelCatalogItem, modelBillingLabel, filterVisibleCatalogModels } from '@/api/llm';
import { byokApi, pickedModelIds } from '@/api/byok';
import { useByokEnabled, useDisabledModels } from '@/hooks/useByokEnabled';
import { cloudApi, cloudBillingApi } from '@/api/cloud';
import { useSettings } from '@/store/settings';
import { useAuth } from '@/hooks/useAuth';
import { useLLMBalance } from '@/hooks/useLLMBalance';
import { PLATFORM_PREFIX, isPlatformModel, isReasoningModel } from './modelUtils';

// 平台/推理档常量与判定函数在 ./modelUtils（react-refresh：组件文件只导出组件，
// 引用方一律从 modelUtils 引，不经本文件 re-export）

// 桌面端（Electron）判定
const isDesktop = !!window.psbDesktop?.isDesktop;

export type TaskType = 'chat' | 'summary' | 'analysis' | 'creative' | 'coding' | 'reasoning' | 'default';

export interface ModelSelectorProps {
  value?: string;
  onChange: (modelId: string) => void;
  taskType?: TaskType;
  showPrice?: boolean;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  /** 平台模型分组（桌面端+已绑定云端账号时显示）。默认全站开启。 */
  allowPlatform?: boolean;
  /** 推理档禁入（图谱构建等批量场景用）：从全部选项中滤除强制思考模型 */
  excludeReasoning?: boolean;
  /** 本地模型禁入（百科编译等本地小模型干不了的场景）：滤除 🏠本地 分组 */
  excludeLocal?: boolean;
  /** 自动填充/跟随总开关：false 时挂载填充与控制台跟随都不改值（手动改选仍生效）。
      聊天页会话级模型锁定用——会话已有消息后绝不被自动逻辑换模型（09-04 混模型事故） */
  autoSync?: boolean;
  /** 挂载时以控制台选择为基准：baseline 首次解析时当前值不同则跟随控制台（聊天页用，
      修「控制台选了系统模型、聊天页停在旧本地默认」的残留） */
  mountFollow?: boolean;
}

interface LocalModelOption {
  id: string;
  name: string;
  provider: 'ollama';
  description: string;
}

const LOCAL_MODELS: LocalModelOption[] = [
  { id: 'ollama-qwen3.5-0.8b', name: 'Qwen 3.5 0.8B（本地）', provider: 'ollama', description: '本地轻量小模型' },
];

function isLocalModel(id?: string): boolean {
  return !!id && id.startsWith('ollama-');
}

function modelPriceScore(m: LLMModelCatalogItem): number {
  return (m.price_input_per_1k || 0) + (m.price_output_per_1k || 0);
}

function recommendSystemModel(models: LLMModelCatalogItem[], taskType: TaskType): string | null {
  // Exclude local (Ollama) models from auto-recommendation: they are listed
  // separately under "本地模型" and are unavailable when the local service is down.
  const cloudModels = models.filter((m) => m.provider !== 'ollama' && !m.id.startsWith('ollama-'));
  if (cloudModels.length === 0) return null;
  const sorted = [...cloudModels].sort((a, b) => modelPriceScore(a) - modelPriceScore(b));
  switch (taskType) {
    case 'summary':
    case 'analysis':
    case 'chat':
    case 'default':
      // Recommend cheapest capable model
      return sorted[0]?.id || null;
    case 'coding':
    case 'reasoning':
    case 'creative':
      // Recommend most capable (most expensive) among top half
      return sorted[Math.min(sorted.length - 1, Math.ceil(sorted.length / 2))]?.id || null;
    default:
      return sorted[0]?.id || null;
  }
}

export function ModelSelector({
  value,
  onChange,
  taskType = 'default',
  showPrice = true,
  className = '',
  placeholder = '选择模型',
  disabled = false,
  allowPlatform = true,
  excludeReasoning = false,
  excludeLocal = false,
  autoSync = true,
  mountFollow = false,
}: ModelSelectorProps) {
  const { data: catalog, isLoading } = useQuery({
    queryKey: ['llmModelCatalog'],
    queryFn: getModelCatalog,
    staleTime: 5 * 60 * 1000,
  });
  const { balance: balanceSummary } = useLLMBalance();
  const ollamaModel = useSettings((s) => s.ollamaModel);
  const activeProvider = useSettings((s) => s.activeProvider);
  const activeModel = useSettings((s) => s.activeModel);
  const { isLoggedIn } = useAuth();
  // BYOK key 状态：以 BYOK 控制台端点为准（slug→已配置）。旧 store.apiKeys 从无注入方
  // （永远空表，所有 BYOK 行恒标〔未填 key〕的隐形 bug，09-05 二期实捕）
  const { data: byokPresetData } = useQuery({
    queryKey: ['byokPresets'],
    queryFn: byokApi.listPresets,
    staleTime: 30000,
  });
  const byokConfigured = useMemo(() => {
    const map: Record<string, boolean> = {};
    (byokPresetData?.presets || []).forEach((p) => { map[p.slug] = p.configured; });
    return map;
  }, [byokPresetData]);
  const byokPicked = useMemo(() => pickedModelIds(byokPresetData), [byokPresetData]);

  // 平台模型分组：仅桌面端 + 允许 + 已绑定云端账号时拉取云端目录/余额
  const { data: cloudStatus, isFetched: cloudStatusFetched } = useQuery({
    queryKey: ['cloudStatus'],
    queryFn: cloudApi.getStatus,
    enabled: allowPlatform && isDesktop,
    staleTime: 30000,
  });
  const platformEnabled = allowPlatform && isDesktop && !!cloudStatus?.bound;
  const { data: cloudCatalog, isFetched: cloudCatalogFetched } = useQuery({
    queryKey: ['cloudModelCatalog'],
    queryFn: cloudBillingApi.catalog,
    enabled: platformEnabled,
    staleTime: 5 * 60 * 1000,
  });
  const { data: cloudBalance } = useQuery({
    queryKey: ['cloudBalance'],
    queryFn: cloudBillingApi.balance,
    enabled: platformEnabled,
    staleTime: 60000,
  });
  // 平台分组只放平台计费模型（is_system 或有价），过滤云端目录里的本地免费模型
  const platformModels = useMemo(() => platformEnabled
    ? (cloudCatalog?.models || []).filter(
        (m) => (m.is_system || (m.price_input_per_1k || 0) > 0 || (m.price_output_per_1k || 0) > 0)
          && !(excludeReasoning && isReasoningModel(m.id))
      )
    : [], [platformEnabled, cloudCatalog?.models, excludeReasoning]);

  // 两套分离（08-15 深夜拍板）：BYOK 自付组只放非系统行（0 价自付模型）；
  // 系统行（is_system，平台按量/免费）一律进下方系统模型分组——
  // 桌面端走 ☁️平台模型分组（platform: 通道），网页端走 ☁️系统模型分组（直选目录 id）
  const byokEnabled = useByokEnabled();
  const disabledModels = useDisabledModels();
  const visibleCatalog = filterVisibleCatalogModels(catalog?.models || [], byokEnabled, disabledModels, isDesktop, byokPicked);
  const systemModels = visibleCatalog.filter((m) => !m.is_system && !(excludeReasoning && isReasoningModel(m.id)));
  // 网页端的系统模型分组（桌面端用云端目录的平台分组代替）
  const webSystemModels = useMemo(
    () => isDesktop ? [] : visibleCatalog.filter((m) => m.is_system && !(excludeReasoning && isReasoningModel(m.id))),
    [visibleCatalog, excludeReasoning],
  );

  const localOptions = useMemo(() => {
    if (excludeLocal) return []; // 本地模型禁入（编译等场景小模型干不了）
    const base = [...LOCAL_MODELS];
    // If user has a different Ollama model in settings, offer it as well
    const customId = `ollama-${ollamaModel}`;
    if (ollamaModel && !base.some((m) => m.id === customId)) {
      base.push({
        id: customId,
        name: `${ollamaModel}（本地）`,
        provider: 'ollama',
        description: '自定义 Ollama 模型',
      });
    }
    return base;
  }, [ollamaModel, excludeLocal]);

  // The model the user picked in the global LLM console (settings store),
  // mapped back to this selector's option id by (provider, provider_model_id),
  // so every page defaults to it instead of an unrelated cheapest pick.
  const consoleDefaultId = useMemo(() => {
    if (!activeModel) return '';
    // 控制台选的平台模型：映射回 platform:{id} 选项（此前无此分支，控制台选平台模型后
    // 各页面选择器映射不到任何选项 → 回落推荐模型，「整站跟随」看起来失效）
    if ((activeProvider || '').toLowerCase() === 'platform') {
      return platformModels.some((m) => m.id === activeModel) ? `${PLATFORM_PREFIX}${activeModel}` : '';
    }
    // active_model 有两种历史写法：provider_model_id（控制台写入）与 catalog id
    // （聊天页兜底写入），反查必须兼容两种，否则跟随随写入方不同而时好时坏。
    // 系统行（is_system）也要参与反查——网页端选系统模型后各页面同样要跟随
    const match = [...systemModels, ...webSystemModels].find(
      (m) =>
        m.provider?.toLowerCase() === (activeProvider || '').toLowerCase() &&
        (m.provider_model_id === activeModel || m.id === activeModel)
    );
    if (match) return match.id;
    if ((activeProvider || '').toLowerCase() === 'ollama') return `ollama-${activeModel}`;
    return '';
  }, [activeProvider, activeModel, systemModels, platformModels, webSystemModels]);

  const recommendedId = useMemo(() => {
    const sysRec = recommendSystemModel(systemModels, taskType);
    return sysRec || localOptions[0]?.id || '';
  }, [systemModels, localOptions, taskType]);

  const hasSetDefault = useRef(false);
  // 控制台映射未就绪时用了推荐模型兜底填充的标记：映射晚到（云端目录/状态同步的
  // 异步跳）时要用真实跟随值替换兜底值，否则页面永远停在推荐模型上
  const autoFilledFallbackRef = useRef(false);
  // 控制台选的是平台模型时，初始填充必须等云端目录链路就绪（cloudStatus → bound →
  // cloudCatalog 两跳都是异步）——否则 platformModels 还是空数组，consoleDefaultId
  // 解析不出，先把推荐模型（如 glm-4.6）填进去，之后 baseline 记录把晚到的
  // platform 值吞掉，页面就永远停在推荐模型上
  const waitingPlatformCatalog =
    (activeProvider || '').toLowerCase() === 'platform' && allowPlatform && isDesktop &&
    (!cloudStatusFetched || (!!cloudStatus?.bound && !cloudCatalogFetched));
  useEffect(() => {
    if (!autoSync) return; // 会话级模型锁定：自动填充关闭
    if (!hasSetDefault.current && !value && isLoggedIn && !isLoading && !waitingPlatformCatalog) {
      // Prefer the console-selected model; only fall back to the cheapest
      // recommendation when there is no console selection.
      const fill = consoleDefaultId || recommendedId;
      if (fill) {
        hasSetDefault.current = true;
        autoFilledFallbackRef.current = !consoleDefaultId;
        onChange(fill);
      }
    }
  }, [value, consoleDefaultId, recommendedId, onChange, isLoggedIn, isLoading, waitingPlatformCatalog, autoSync]);

  // 全站一致：右下角 LLM 控制台换模型后，各页面选择器跟随切换。
  // 挂载时以控制台当前选择为基准，之后控制台每次变更都同步到本选择器；
  // 用户仍可在本页手动改选，但下一次控制台变更会再次统一。
  const lastConsoleIdRef = useRef<string | null>(null);
  // 全部可选值集合：判断当前值是否为失效残留（如旧版 defaultLLM='ollama' 不在选项里），
  // 残留值不能让「以控制台为基准」失效
  const allOptionValues = useMemo(() => {
    const s = new Set<string>();
    localOptions.forEach((m) => s.add(m.id));
    systemModels.forEach((m) => s.add(m.id));
    webSystemModels.forEach((m) => s.add(m.id));
    platformModels.forEach((m) => s.add(`${PLATFORM_PREFIX}${m.id}`));
    return s;
  }, [localOptions, systemModels, webSystemModels, platformModels]);
  useEffect(() => {
    // 会话级模型锁定：锁定期间一切自动跟随/残留替换关闭（手动改选仍走 select onChange）
    if (!autoSync) return;
    // 游客（未登录只读演示）不做跟随/接管：残留值替换会触发 onChange →
    // ChatInputBar 对未登录用户弹登录框，挂载期自动弹窗把演示页挡住（08-16 实证）
    if (!isLoggedIn) return;
    // 控制台映射为空（模型被禁用/删除、目录里已查不到）且当前值不在任何选项里：
    // 不能提前 return 留死值——回退到推荐模型/本地默认，让选择器恢复可用。
    // 平台链路未就绪（waitingPlatformCatalog）时除外：那只是映射晚到，等云端目录
    // 到了由下方 baseline 分支用真实跟随值替换。
    if (!consoleDefaultId) {
      if (!waitingPlatformCatalog && value && !allOptionValues.has(value)) {
        const fallback = recommendedId || localOptions[0]?.id || '';
        if (fallback) {
          hasSetDefault.current = true;
          onChange(fallback);
        }
      }
      return;
    }
    if (lastConsoleIdRef.current === null) {
      lastConsoleIdRef.current = consoleDefaultId;
      // 映射晚到且当前值是我们的兜底填充 → 用真实跟随值替换
      if (autoFilledFallbackRef.current && value && value !== consoleDefaultId) {
        autoFilledFallbackRef.current = false;
        hasSetDefault.current = true;
        onChange(consoleDefaultId);
        return;
      }
      // 当前值是失效残留（不在任何选项里）→ 直接以控制台选择为准
      if (value && !allOptionValues.has(value) && value !== consoleDefaultId) {
        hasSetDefault.current = true;
        onChange(consoleDefaultId);
        return;
      }
      // mountFollow（聊天页）：挂载时以控制台选择为基准——当前值是旧默认残留
      // （如控制台选了系统模型但聊天页停在旧本地默认）时跟随控制台
      if (mountFollow && value && value !== consoleDefaultId) {
        hasSetDefault.current = true;
        onChange(consoleDefaultId);
      }
      return;
    }
    if (consoleDefaultId !== lastConsoleIdRef.current) {
      lastConsoleIdRef.current = consoleDefaultId;
      if (value && value !== consoleDefaultId) {
        hasSetDefault.current = true;
        onChange(consoleDefaultId);
      }
    }
  }, [consoleDefaultId, value, onChange, allOptionValues, isLoggedIn, waitingPlatformCatalog, recommendedId, localOptions, autoSync, mountFollow]);

  const selectedModel = useMemo(() => {
    if (!value) return null;
    if (isPlatformModel(value)) {
      const rawId = value.slice(PLATFORM_PREFIX.length);
      return platformModels.find((m) => m.id === rawId) || null;
    }
    if (isLocalModel(value)) {
      return localOptions.find((m) => m.id === value) || null;
    }
    return systemModels.find((m) => m.id === value) || null;
  }, [value, localOptions, systemModels, platformModels]);

  const renderOption = (id: string, name: string, desc: string, priceNode?: React.ReactNode) => (
    <option key={id} value={id}>
      {name} {priceNode ? `· ${priceNode}` : ''}
    </option>
  );

  return (
    <div className={`relative ${className}`}>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled || isLoading}
            className="w-full appearance-none pl-9 pr-8 py-2 rounded-[2px] bg-white/[0.03] border border-white/[0.08] text-sm text-text-primary focus:outline-none focus:border-info disabled:opacity-50"
          >
            {!value && <option value="">{placeholder}</option>}

            {localOptions.length > 0 && (
              <optgroup label="🏠 本地模型（免费）">
                {localOptions.map((m) =>
                  renderOption(m.id, m.name, m.description, '本地免费')
                )}
              </optgroup>
            )}

            {systemModels.length > 0 && (
              <optgroup label="🔑 BYOK 模型（自付）">
                {systemModels.map((m) => {
                  const isRec = m.id === recommendedId;
                  const prov = (m.provider || '').toLowerCase();
                  // 通道小字（09-01 拍板，同名两行必须可辨）：本地 ollama 行不算 BYOK，
                  // 外来 BYOK 行标自付、未填 key 前置警示
                  const channelTag = prov === 'ollama'
                    ? '〔本地免费〕'
                    : byokConfigured[prov] ? '〔BYOK 自付〕' : '〔BYOK 自付·未填 key〕';
                  const priceLabel = showPrice ? modelBillingLabel(m) : '';
                  return (
                    <option key={m.id} value={m.id}>
                      {m.name} {channelTag} {priceLabel !== 'BYOK 自付' && priceLabel !== '免费' ? priceLabel : ''} {isRec ? '· 推荐' : ''}
                    </option>
                  );
                })}
              </optgroup>
            )}

            {platformModels.length > 0 && (
              <optgroup label="☁️ 系统模型（平台按量）">
                {platformModels.map((m) => {
                  const priceLabel = showPrice ? modelBillingLabel(m) : '';
                  return (
                    <option key={`${PLATFORM_PREFIX}${m.id}`} value={`${PLATFORM_PREFIX}${m.id}`}>
                      {m.name} 〔按量〕 {priceLabel !== '免费' ? priceLabel : ''}
                    </option>
                  );
                })}
              </optgroup>
            )}

            {webSystemModels.length > 0 && (
              <optgroup label="☁️ 系统模型（平台按量）">
                {webSystemModels.map((m) => {
                  const priceLabel = showPrice ? modelBillingLabel(m) : '';
                  return (
                    <option key={m.id} value={m.id}>
                      {m.name} 〔按量〕 {priceLabel !== '免费' ? priceLabel : ''}
                    </option>
                  );
                })}
              </optgroup>
            )}
          </select>
          <Cpu className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" />
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" />
        </div>
      </div>

      {selectedModel && (
        <div className="mt-1.5 flex items-center gap-2 text-xs text-text-muted">
          {isLocalModel(selectedModel.id) ? (
            isDesktop ? (
              <>
                <Server className="w-3 h-3 text-success" />
                <span className="text-success">本地调用，不计费</span>
              </>
            ) : (
              // 网页版「本地模型」实际跑在云端服务器 CPU 上（09-05 容量事故复盘拍板）：
              // 如实告知并引导下载桌面端——模型跑在用户自己电脑上才真免费不限量
              <>
                <Server className="w-3 h-3 text-warning" />
                <span>
                  服务器代跑，资源有限、高峰可能排队；
                  <a href="/download/PSB-Setup-0.2.188.exe" className="text-warning underline hover:text-warning/80">下载桌面端</a>
                  在你自己电脑上跑，免费不限量
                </span>
              </>
            )
          ) : isPlatformModel(value) ? (
            <>
              <Cloud className="w-3 h-3 text-info" />
              <span>云端按量扣费（扣绑定账号的云端余额）</span>
            </>
          ) : (
            <>
              <Cloud className="w-3 h-3 text-info" />
              {(selectedModel as LLMModelCatalogItem).is_system ? (
                <span>平台按量（扣云端余额）</span>
              ) : ((selectedModel as LLMModelCatalogItem).provider || '').toLowerCase() === 'ollama' ? (
                <span className="text-success">本地调用，不计费</span>
              ) : !byokConfigured[((selectedModel as LLMModelCatalogItem).provider || '').toLowerCase()] ? (
                <span className="text-warning">BYOK 自付·未填 key（去 设置 → BYOK 自付 填写后才可用）</span>
              ) : (
                <span>BYOK 自付（你的厂商 key 直结）</span>
              )}
            </>
          )}
        </div>
      )}

      {/* Balance info：平台模型看云端余额（跳云端充值），其余看本地余额 */}
      {isPlatformModel(value) && cloudBalance ? (
        cloudBalance.balance < 0.1 ? (
          <div className="mt-1.5 flex items-center gap-1.5 text-xs text-warning">
            <AlertCircle className="w-3 h-3" />
            <span>云端余额不足，<a href="https://grzhishiku.com/topup" target="_blank" rel="noreferrer" className="underline hover:text-warning/80">去云端充值</a></span>
          </div>
        ) : (
          <div className="mt-1.5 flex items-center gap-1.5 text-xs text-text-muted">
            <Wallet className="w-3 h-3 text-success" />
            <span>云端余额 ¥{cloudBalance.balance.toFixed(2)}，<a href="https://grzhishiku.com/topup" target="_blank" rel="noreferrer" className="underline hover:text-text-primary">充值</a></span>
          </div>
        )
      ) : null}
      {!isLocalModel(value) && !isPlatformModel(value) && balanceSummary && (
        balanceSummary.balance < 0.1 ? (
          <div className="mt-1.5 flex items-center gap-1.5 text-xs text-warning">
            <AlertCircle className="w-3 h-3" />
            <span>余额不足，<a href="/topup" className="underline hover:text-warning/80">去充值</a></span>
          </div>
        ) : (
          <div className="mt-1.5 flex items-center gap-1.5 text-xs text-text-muted">
            <Wallet className="w-3 h-3 text-success" />
            <span>可用余额 ¥{balanceSummary.balance.toFixed(2)}，<a href="/topup" className="underline hover:text-text-primary">充值</a></span>
          </div>
        )
      )}
    </div>
  );
}

export default ModelSelector;
